/**
 * Lightbox — H5 `#imageLightbox` mirror (index.html:633-639, styles.css:5132-5260,
 * main.js:8846-8889 + 3373-3408 keyboard binds).
 *
 * Singleton portal mounted once at App.tsx top level. Visibility driven by
 * useLightboxStore; never unmounts so AP2-compliant opacity transitions
 * fire on the `.is-open` className flip without rAF dance.
 *
 * H5 anti-patterns avoided:
 *   - AP2: NO `requestAnimationFrame(() => add("is-open"))`; React state
 *     toggles className declaratively, CSS transition fires on attribute
 *     change of an already-mounted element.
 *   - AP9: keyboard listeners attached via useEffect only while open,
 *     cleaned up on close. H5 main.js:3401-3404 leaks a permanent
 *     `document.addEventListener("keydown", ...)`.
 *   - AP6: backdrop / arrow / close buttons use React onClick props, not
 *     `el.addEventListener` at module init.
 *
 * Optimizations beyond H5 baseline:
 *   - Adjacent-image prefetch (prev + next) via requestIdleCallback so
 *     ArrowLeft/Right swaps are instant; H5 fetched on demand → flicker.
 *   - prefers-reduced-motion respected (Lightbox.css collapses transitions).
 *   - Body scroll locked while open (H5 didn't, allowing accidental scroll
 *     bleed-through on iOS Safari).
 */

import { useEffect, type MouseEvent } from "react";
import { createPortal } from "react-dom";

import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion.js";
import { useLightboxStore } from "@/shared/stores/lightboxStore.js";

async function downloadMediaToDevice(url: string) {
  const res = await fetch(url, { credentials: "omit", mode: "cors" });
  if (!res.ok) return;
  const blob = await res.blob();
  const subtype = (blob.type.split("/")[1] ?? "").toLowerCase().replace("jpeg", "jpg").split("+")[0];
  const ext = subtype && /^[a-z0-9]+$/.test(subtype) ? subtype : "jpg";
  const filename = `tree-hole-${Date.now()}.${ext}`;
  if (typeof File !== "undefined" && navigator.canShare) {
    const file = new File([blob], filename, { type: blob.type });
    if (navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: filename });
      return;
    }
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

import "./Lightbox.css";

type IdleHandle = number;

function scheduleIdle(cb: () => void): { cancel: () => void } {
  if (typeof window === "undefined") return { cancel: () => undefined };
  const ric = (
    window as unknown as {
      requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    }
  ).requestIdleCallback;
  const cic = (
    window as unknown as { cancelIdleCallback?: (handle: number) => void }
  ).cancelIdleCallback;
  if (ric && cic) {
    const id = ric(cb, { timeout: 1000 });
    return { cancel: () => cic(id) };
  }
  const id = window.setTimeout(cb, 200) as unknown as IdleHandle;
  return { cancel: () => window.clearTimeout(id as unknown as number) };
}

export function Lightbox() {
  const images = useLightboxStore((state) => state.images);
  const index = useLightboxStore((state) => state.index);
  const isOpen = useLightboxStore((state) => state.isOpen);
  const showOriginal = useLightboxStore((state) => state.showOriginal);
  const close = useLightboxStore((state) => state.close);
  const step = useLightboxStore((state) => state.step);
  const setShowOriginal = useLightboxStore((state) => state.setShowOriginal);
  // §9.3: when the user/OS asks for reduced motion, skip the
  // adjacent-image idle prefetch entirely. Rationale:
  //   1. Prefetch's only purpose is to make the next-image *transition*
  //      feel instant, but reduce-motion users have already opted out of
  //      transitions; loading the next image early is wasted bytes.
  //   2. Some accessibility users explicitly turn on reduce-motion in
  //      tandem with data-saver / metered-network preferences. Skipping
  //      idle network work matches that intent.
  const reduceMotion = usePrefersReducedMotion();

  // Keyboard nav — only while open. Mirrors H5 main.js:3401-3404 but with
  // proper cleanup (AP9 fix).
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      } else if (event.key === "ArrowLeft") {
        event.stopPropagation();
        step(-1);
      } else if (event.key === "ArrowRight") {
        event.stopPropagation();
        step(1);
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, close, step]);

  // Body scroll lock while open. H5 didn't bother; iOS Safari leaks scroll
  // through to the underlying detail panel without this.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Prefetch adjacent images (prev + next) on idle. Decoders warm up so
  // ArrowLeft/Right transitions are instant. Cancelled when the index
  // shifts to a different pair or the lightbox closes.
  //
  // §9.3: skip entirely when reduce-motion is on — see hook subscription
  // above for rationale (no transition to smooth → no point spending the
  // bytes warming the cache).
  useEffect(() => {
    if (!isOpen || images.length <= 1) return;
    if (reduceMotion) return;
    const len = images.length;
    const nextSrc = images[(index + 1) % len]?.src;
    const prevSrc = images[(index - 1 + len) % len]?.src;
    const handle = scheduleIdle(() => {
      // `new Image()` triggers HTTP fetch + browser image cache without
      // attaching to the DOM. preferred over <link rel=prefetch> because
      // we want decode priority similar to the visible <img>.
      if (nextSrc) {
        const next = new Image();
        next.src = nextSrc;
      }
      if (prevSrc && prevSrc !== nextSrc) {
        const prev = new Image();
        prev.src = prevSrc;
      }
    });
    return () => handle.cancel();
  }, [isOpen, images, index, reduceMotion]);

  const current = images[index];
  const hasOriginal = Boolean(current?.originalSrc && current.originalSrc !== current.src);
  const displaySrc = showOriginal && hasOriginal ? current!.originalSrc! : current?.src;
  const className = isOpen ? "image-lightbox is-open" : "image-lightbox";
  const showArrows = images.length > 1;

  function handleBackdropClick(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) close();
  }

  const node = (
    <div
      className={className}
      role="dialog"
      aria-modal="true"
      aria-label="图片放大预览"
      aria-hidden={!isOpen}
      {...({ inert: isOpen ? undefined : "" } as Record<string, string | undefined>)}
      onClick={handleBackdropClick}
    >
      <button type="button" className="lightbox-close" aria-label="关闭预览" onClick={close}>
        <span />
        <span />
      </button>
      <button
        type="button"
        className="lightbox-arrow lightbox-arrow--prev"
        aria-label="上一张"
        hidden={!showArrows}
        onClick={(event) => { event.stopPropagation(); step(-1); }}
      >
        <span />
      </button>
      <button
        type="button"
        className="lightbox-arrow lightbox-arrow--next"
        aria-label="下一张"
        hidden={!showArrows}
        onClick={(event) => { event.stopPropagation(); step(1); }}
      >
        <span />
      </button>
      <div className="lightbox-toolbar" hidden={!hasOriginal && !current?.src}>
        {hasOriginal && (
          <button
            type="button"
            className="lightbox-toolbar-btn"
            aria-pressed={showOriginal}
            onClick={(e) => { e.stopPropagation(); setShowOriginal(true); }}
            disabled={showOriginal}
          >
            {showOriginal ? "已显示原图" : "查看原图"}
          </button>
        )}
        {current?.src && (
          <button
            type="button"
            className="lightbox-toolbar-btn"
            onClick={(e) => {
              e.stopPropagation();
              void downloadMediaToDevice(current.originalSrc ?? current.src);
            }}
          >
            保存到相册
          </button>
        )}
      </div>
      <figure
        className="lightbox-figure"
        onClick={(event) => { if (event.target === event.currentTarget) close(); }}
      >
        {current ? (
          <img
            src={displaySrc}
            alt={current.alt || "图片预览"}
            onClick={(event) => { event.stopPropagation(); close(); }}
          />
        ) : null}
        <figcaption className="lightbox-caption" data-empty={current?.alt ? "false" : "true"}>
          {current?.alt ?? ""}
        </figcaption>
      </figure>
    </div>
  );

  // SSR safety: only mount portal client-side (document available).
  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}
